import { parseSseEvent, type SseParsedEvent } from "./sseParser";

const CRLF_REGEX = /\r\n/g;
const CR_REGEX = /\r/g;
const LF = "\n";
const SSE_EVENT_DELIMITER = "\n\n";

export interface MessageStreamerParams {
  reader: ReadableStreamDefaultReader<string>;
  /**
   * Called whenever a full SSE event is parsed.
   */
  onEvent: (event: SseParsedEvent) => void | Promise<void>;
  /**
   * Optional abort signal; when aborted, we stop reading the stream.
   */
  abortSignal?: AbortSignal;
}

export interface MessageStreamerResult {
  /**
   * If the stream ended with an unfinished buffer (no trailing "\n\n"), it will be returned here.
   */
  trailingBuffer: string;
}

export const streamMessagesFromResponse = async (
  params: MessageStreamerParams
): Promise<MessageStreamerResult> => {
  const { reader, onEvent, abortSignal } = params;

  let buffer = "";

  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      if (abortSignal?.aborted) {
        try {
          await reader.cancel();
        } catch {}
        break;
      }

      let chunk: ReadableStreamReadResult<string>;
      try {
        chunk = await reader.read();
      } catch (e) {
        // Aborting the underlying fetch/stream often makes `read()` throw.
        // Treat user-stop as a clean exit.
        if (abortSignal?.aborted) {
          break;
        }
        throw e;
      }

      const { value, done } = chunk;
      if (done) {
        break;
      }

      // Normalize CRLF → LF so delimiter splitting is consistent even if BE uses \r\n\r\n.
      buffer += value.replace(CRLF_REGEX, LF).replace(CR_REGEX, LF);

      const events = buffer.split(SSE_EVENT_DELIMITER);
      buffer = events.pop() ?? "";

      // Process all complete events
      for (const rawEvent of events) {
        const parsed = parseSseEvent(rawEvent);
        if (!!parsed) {
          await onEvent(parsed);
        }
      }
    }
  } finally {
    try {
      reader.releaseLock();
    } catch {}
  }

  return { trailingBuffer: buffer };
};
